/**
 * Tableau des heures supplémentaires
 * @module components/heures-sup/HeuresSupTable
 */

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Edit2, Trash2, Clock, Moon, Calendar } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

import type { HeureSup, HeureSupAvecMembre, TypeHeureSup } from '@/types/heures-sup.types';
import { formatHeures, formatCoutHeuresSup, formatMajoration } from '@/lib/calculs-heures-sup';

interface HeuresSupTableProps {
  heuresSup: HeureSupAvecMembre[];
  isLoading: boolean;
  onEdit: (entry: HeureSup) => void;
  onDelete: (id: string) => void;
}

// Badge pour le type d'heure
function TypeHeureBadge({ type }: { type: TypeHeureSup }) {
  switch (type) {
    case 'nuit':
      return (
        <Badge variant="outline" className="border-purple-500/50 text-purple-400">
          <Moon className="w-3 h-3 mr-1" />
          Nuit
        </Badge>
      );
    case 'ferie':
      return (
        <Badge variant="outline" className="border-red-500/50 text-red-400">
          <Calendar className="w-3 h-3 mr-1" />
          Férié
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="border-amber-500/50 text-amber-400">
          <Clock className="w-3 h-3 mr-1" />
          Standard
        </Badge>
      );
  }
}

// Skeleton de chargement
function TableSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex gap-4 p-4 border rounded-lg">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-20" />
        </div>
      ))}
    </div>
  );
}

export default function HeuresSupTable({
  heuresSup,
  isLoading,
  onEdit,
  onDelete,
}: HeuresSupTableProps) {
  if (isLoading) {
    return <TableSkeleton />;
  }

  if (heuresSup.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>Aucune heure supplémentaire enregistrée</p>
        <p className="text-sm">Cliquez sur "Ajouter des heures sup" pour commencer</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-y-hidden overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Membre</TableHead>
            <TableHead className="text-center">Heures</TableHead>
            <TableHead>Type</TableHead>
            <TableHead className="text-center">Majoration</TableHead>
            <TableHead className="text-right">Coût</TableHead>
            <TableHead className="w-24"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {heuresSup.map((hs) => (
            <TableRow key={hs.id}>
              <TableCell className="font-medium">
                {format(parseISO(hs.date_travail), 'dd/MM/yyyy', { locale: fr })}
              </TableCell>
              <TableCell>
                {hs.membre ? (
                  <span>
                    {hs.membre.prenom} {hs.membre.nom.charAt(0)}.
                  </span>
                ) : (
                  <span className="text-muted-foreground">Membre inconnu</span>
                )}
              </TableCell>
              <TableCell className="text-center font-mono">
                {formatHeures(hs.nombre_heures)}
              </TableCell>
              <TableCell>
                <TypeHeureBadge type={hs.type_heure} />
              </TableCell>
              <TableCell className="text-center text-amber-500 font-medium">
                {formatMajoration(hs.taux_majoration)}
              </TableCell>
              <TableCell className="text-right font-medium">
                {formatCoutHeuresSup(hs.cout_total)}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onEdit(hs)}
                    className="h-8 w-8"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer cette entrée ?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Cette action est irréversible. Les heures supplémentaires de{' '}
                          <strong>
                            {hs.membre?.prenom} {hs.membre?.nom}
                          </strong>{' '}
                          du {format(parseISO(hs.date_travail), 'dd/MM/yyyy', { locale: fr })} seront supprimées.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => onDelete(hs.id)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Supprimer
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
